import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../config/api';
import { extractErrorMessage } from '../utils/extractError';

/* ---------- types ---------- */
interface HourData {
  hour: number;
  monitor_count: number;
  monitors: string[];
}

interface CoverageData {
  date: string;
  hours: HourData[];
  summary: {
    peak_hour: number;
    peak_count: number;
    uncovered_hours: number[];
  };
}

function formatDateParam(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatHourLabel(hour: number): string {
  if (hour === 0) return '12 AM';
  if (hour < 12) return `${hour} AM`;
  if (hour === 12) return '12 PM';
  return `${hour - 12} PM`;
}

export default function CoveragePage() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();

  const [date, setDate] = useState(() => formatDateParam(new Date()));
  const [data, setData] = useState<CoverageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedHour, setSelectedHour] = useState<number | null>(null);

  const fetchCoverage = useCallback(async (dateStr: string) => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/admin/dashboard/coverage?date=${dateStr}`);
      setData(res.data);
      setSelectedHour(null);
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'Failed to load coverage data.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCoverage(date);
  }, [date, fetchCoverage]);

  // Ensure 24 hours of data even if API returns fewer
  const hourMap = new Map<number, HourData>();
  if (data?.hours) {
    data.hours.forEach((h) => hourMap.set(h.hour, h));
  }
  const allHours: HourData[] = Array.from({ length: 24 }, (_, i) =>
    hourMap.get(i) || { hour: i, monitor_count: 0, monitors: [] }
  );

  const maxCount = Math.max(1, ...allHours.map((h) => h.monitor_count));

  function getBarColor(count: number): string {
    if (count === 0) return 'var(--status-critical-muted)';
    const intensity = Math.min(count / maxCount, 1);
    const alpha = 0.3 + intensity * 0.7;
    return `rgba(23,178,106,${alpha})`;
  }

  if (!isAdmin) {
    return (
      <div style={styles.page}>
        <main style={styles.main}>
          <div style={styles.accessDenied}>
            <span style={{ fontSize: 36, color: 'var(--status-critical)', display: 'block', marginBottom: 12 }}>&#9888;</span>
            <h2 style={{ margin: '0 0 8px', fontSize: 'var(--text-xl)', fontFamily: 'var(--font-display)', color: 'var(--status-critical)' }}>Access Denied</h2>
            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: 'var(--text-md)', fontFamily: 'var(--font-body)' }}>Lead or Admin role required.</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <main style={styles.main}>
        <button onClick={() => navigate('/admin')} style={styles.backBtn}>
          &#8249; Back to Admin
        </button>

        <div style={styles.titleRow}>
          <h2 style={styles.pageTitle}>Coverage Timeline</h2>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            style={styles.datePicker}
          />
        </div>

        {loading && <p style={styles.statusText}>Loading coverage data...</p>}
        {error && <div style={styles.errorBanner}>{error}</div>}

        {!loading && !error && data && (
          <>
            {/* Summary cards */}
            <div style={styles.summaryRow}>
              <div style={styles.summaryCard}>
                <div style={styles.summaryValue}>
                  {data.summary.peak_hour != null ? formatHourLabel(data.summary.peak_hour) : '—'}
                </div>
                <div style={styles.summaryLabel}>Peak Coverage Hour</div>
                {data.summary.peak_count > 0 && (
                  <div style={styles.summarySubtext}>{data.summary.peak_count} monitor{data.summary.peak_count !== 1 ? 's' : ''}</div>
                )}
              </div>
              <div style={{
                ...styles.summaryCard,
                ...(data.summary.uncovered_hours.length > 0 ? { borderColor: 'rgba(255,71,87,0.4)', borderLeftColor: 'var(--status-critical)' } : {}),
              }}>
                <div style={{
                  ...styles.summaryValue,
                  color: data.summary.uncovered_hours.length > 0 ? 'var(--status-critical)' : 'var(--status-success)',
                }}>
                  {data.summary.uncovered_hours.length}
                </div>
                <div style={styles.summaryLabel}>Uncovered Hours</div>
                {data.summary.uncovered_hours.length > 0 && (
                  <div style={styles.summarySubtext}>
                    {data.summary.uncovered_hours
                      .map(formatHourLabel)
                      .join(', ')}
                  </div>
                )}
              </div>
            </div>

            {/* 24-hour timeline */}
            <div style={styles.timelineCard}>
              <h3 style={styles.sectionTitle}>Hourly Coverage</h3>
              <div style={styles.timeline}>
                {allHours.map((h) => (
                  <div
                    key={h.hour}
                    style={styles.hourCol}
                    onClick={() => setSelectedHour(selectedHour === h.hour ? null : h.hour)}
                  >
                    <div style={styles.barContainer}>
                      <div style={{
                        ...styles.bar,
                        height: h.monitor_count > 0 ? Math.max(24, Math.round((h.monitor_count / maxCount) * 220)) : 16,
                        background: getBarColor(h.monitor_count),
                        border: selectedHour === h.hour ? '2px solid var(--accent)' : '1px solid transparent',
                      }}>
                        {h.monitor_count > 0 && (
                          <span style={styles.barCount}>{h.monitor_count}</span>
                        )}
                      </div>
                    </div>
                    <div style={{
                      ...styles.hourLabel,
                      color: h.monitor_count === 0 ? 'var(--status-critical)' : 'var(--text-secondary)',
                      fontWeight: selectedHour === h.hour ? 700 : 400,
                    }}>
                      {h.hour}
                    </div>
                  </div>
                ))}
              </div>

              {/* Legend */}
              <div style={styles.legend}>
                <div style={styles.legendItem}>
                  <span style={{ ...styles.legendDot, background: 'var(--status-success)' }} />
                  <span>Covered</span>
                </div>
                <div style={styles.legendItem}>
                  <span style={{ ...styles.legendDot, background: 'var(--status-critical-muted)' }} />
                  <span>Uncovered</span>
                </div>
              </div>
            </div>

            {/* Selected hour detail */}
            {selectedHour != null && (() => {
              const hourData = allHours[selectedHour];
              return (
                <div style={styles.hourDetail}>
                  <h4 style={styles.hourDetailTitle}>
                    {formatHourLabel(selectedHour)} — {formatHourLabel((selectedHour + 1) % 24)}
                  </h4>
                  {hourData.monitor_count === 0 ? (
                    <p style={{ margin: 0, color: 'var(--status-critical)', fontSize: 'var(--text-md)', fontFamily: 'var(--font-body)' }}>No monitors active during this hour.</p>
                  ) : (
                    <div>
                      <p style={{ margin: '0 0 8px', color: 'var(--text-secondary)', fontSize: 'var(--text-base)', fontFamily: 'var(--font-body)' }}>
                        {hourData.monitor_count} monitor{hourData.monitor_count !== 1 ? 's' : ''} active:
                      </p>
                      <div style={styles.monitorChips}>
                        {hourData.monitors.map((name, i) => (
                          <span key={i} style={styles.chip}>{name}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </>
        )}
      </main>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    background: 'var(--bg-base)',
  },
  main: {
    maxWidth: 1060,
    margin: '0 auto',
    padding: 'var(--space-8) var(--space-6)',
  },
  backBtn: {
    display: 'inline-block',
    background: 'none',
    border: 'none',
    color: 'var(--accent-text)',
    fontSize: 'var(--text-md)',
    fontWeight: 500,
    fontFamily: 'var(--font-body)',
    cursor: 'pointer',
    padding: 0,
    marginBottom: 'var(--space-5)',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 'var(--space-6)',
  },
  pageTitle: {
    fontSize: 'var(--text-xl)',
    fontWeight: 600,
    fontFamily: 'var(--font-display)',
    color: 'var(--text-primary)',
    margin: 0,
  },
  datePicker: {
    padding: '8px 14px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border-default)',
    background: 'var(--input-bg)',
    color: 'var(--text-primary)',
    fontSize: 'var(--text-md)',
    fontFamily: 'var(--font-body)',
    cursor: 'pointer',
    colorScheme: 'dark',
  },
  statusText: {
    color: 'var(--text-secondary)',
    fontSize: 'var(--text-md)',
    fontFamily: 'var(--font-body)',
  },
  errorBanner: {
    background: 'var(--status-critical-muted)',
    color: 'var(--status-critical)',
    padding: '12px 16px',
    borderRadius: 'var(--radius-lg)',
    fontSize: 'var(--text-md)',
    fontFamily: 'var(--font-body)',
    border: '1px solid rgba(255,71,87,0.3)',
    marginBottom: 'var(--space-4)',
  },
  accessDenied: {
    background: 'var(--bg-surface)',
    borderRadius: 'var(--radius-xl)',
    border: '1px solid rgba(255,71,87,0.3)',
    padding: '40px 32px',
    textAlign: 'center',
  },

  // Summary
  summaryRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 'var(--space-3)',
    marginBottom: 'var(--space-5)',
  },
  summaryCard: {
    background: 'var(--bg-surface)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--border-subtle)',
    borderLeft: '3px solid var(--accent)',
    padding: '20px 24px',
    textAlign: 'center',
  },
  summaryValue: {
    fontSize: 'var(--text-2xl)',
    fontWeight: 700,
    fontFamily: 'var(--font-mono)',
    color: 'var(--text-primary)',
    lineHeight: 1,
    marginBottom: 6,
  },
  summaryLabel: {
    fontSize: 'var(--text-xs)',
    fontWeight: 500,
    fontFamily: 'var(--font-body)',
    color: 'var(--text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  summarySubtext: {
    fontSize: 'var(--text-sm)',
    fontFamily: 'var(--font-mono)',
    color: 'var(--text-secondary)',
    marginTop: 4,
  },

  // Timeline
  timelineCard: {
    background: 'var(--bg-surface)',
    borderRadius: 'var(--radius-xl)',
    border: '1px solid var(--border-subtle)',
    padding: '20px 24px',
    marginBottom: 'var(--space-5)',
  },
  sectionTitle: {
    fontSize: 'var(--text-lg)',
    fontWeight: 600,
    fontFamily: 'var(--font-display)',
    color: 'var(--text-primary)',
    margin: '0 0 18px',
  },
  timeline: {
    display: 'flex',
    gap: 2,
    alignItems: 'flex-end',
    height: 300,
  },
  hourCol: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    cursor: 'pointer',
    height: '100%',
  },
  barContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    width: '100%',
  },
  bar: {
    borderRadius: '3px 3px 0 0',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    paddingTop: 4,
    transition: 'height 0.3s ease',
    minHeight: 8,
  },
  barCount: {
    fontSize: 'var(--text-xs)',
    fontWeight: 700,
    fontFamily: 'var(--font-mono)',
    color: '#fff',
  },
  hourLabel: {
    fontSize: 'var(--text-xs)',
    fontFamily: 'var(--font-mono)',
    marginTop: 4,
    textAlign: 'center',
  },
  legend: {
    display: 'flex',
    gap: 20,
    justifyContent: 'center',
    marginTop: 14,
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 'var(--text-sm)',
    fontFamily: 'var(--font-body)',
    color: 'var(--text-secondary)',
  },
  legendDot: {
    width: 10,
    height: 10,
    borderRadius: 2,
    display: 'inline-block',
  },

  // Hour detail
  hourDetail: {
    background: 'var(--bg-surface-overlay)',
    borderRadius: 'var(--radius-xl)',
    border: '1px solid var(--border-default)',
    padding: '18px 24px',
    marginBottom: 'var(--space-5)',
    boxShadow: 'var(--shadow-md)',
  },
  hourDetailTitle: {
    fontSize: 'var(--text-md)',
    fontWeight: 600,
    fontFamily: 'var(--font-display)',
    color: 'var(--text-primary)',
    margin: '0 0 10px',
  },
  monitorChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
  },
  chip: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: 'var(--radius-md)',
    background: 'var(--accent-muted)',
    color: 'var(--accent-text)',
    fontSize: 'var(--text-sm)',
    fontWeight: 500,
    fontFamily: 'var(--font-body)',
  },
};
